import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CombinedReadonlyDirective } from '../../../../../../../directives/combined-readonly.directive';
import { ActivityEditDialogData, ActivityMuseum, MuseumData, Ticket, TripDay } from '../../../../../../../models';
import { ConfirmationDialogComponent } from '../../../../../../shared/confirmation-dialog/confirmation-dialog.component';
import { InputLinkComponent } from '../../../../../../shared/input-link/input-link.component';
import { InputMonetaryAmountComponent } from '../../../../../../shared/input-monetary-amount/input-monetary-amount.component';
import { InputTicketListComponent } from '../../../../../../shared/input-ticket-list/input-ticket-list.component';
import { InputTimeComponent } from '../../../../../../shared/input-time/input-time.component';
import { MuseumActivityMapper, isActivityWithTimeOverlapping, isEntity } from '../../../../../../shared/utils/activity-utils';

@Component({
  selector: 'museum-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    DragDropModule,
    TranslateModule,
    InputTicketListComponent,
    InputMonetaryAmountComponent,
    InputTimeComponent,
    InputLinkComponent,
    CombinedReadonlyDirective
  ],
  templateUrl: './museum-edit-dialog.component.html',
  styleUrl: './museum-edit-dialog.component.css'
})
export class MuseumEditDialogComponent {

  form!: FormGroup;

  activityMuseum!: ActivityMuseum;
  activityEditIndex?: number;

  tickets: Ticket[] = [];

  currentDay!: TripDay;

  constructor(
    private museumActivityMapper: MuseumActivityMapper,
    @Inject(MAT_DIALOG_DATA) public data: ActivityEditDialogData,
    private dialogRef: MatDialogRef<MuseumEditDialogComponent>,
    private translateService: TranslateService,
    private confirmationDialog: MatDialog
  ) {
    if (isEntity(data.activityData)) {
      this.activityMuseum = this.museumActivityMapper.mapFromEntityToActivity(data.activityData as MuseumData);
    } else {
      this.activityMuseum = data.activityData as ActivityMuseum;
    }
    // in case of edit existing activity get the index of it
    this.activityEditIndex = data.activityEditIndex;
    this.currentDay = data.currentDay;
    this.initializeEditForm();

  }

  private initializeEditForm() {
    this.form = new FormGroup({
      name: new FormControl({ value: this.activityMuseum.name, disabled: true }),

      description: new FormControl(
        this.activityMuseum?.description ?? null),

      disclaimer: new FormControl(
        this.activityMuseum?.disclaimer ?? null),

      notes: new FormControl(
        this.activityMuseum?.notes ?? null),

      address: new FormControl(
        this.activityMuseum?.address ?? null),

      fullPrice: new FormControl(
        this.activityMuseum?.fullPrice ?? null),

      reducedPrice: new FormControl(
        this.activityMuseum?.reducedPrice ?? null),

      startTime: new FormControl(
        this.activityMuseum?.startTime ?? null),

      endTime: new FormControl(
        this.activityMuseum?.endTime ?? null),
    });
    this.tickets = this.activityMuseum?.tickets ?? [];
  }

  confirm() {
    if (this.form.valid && this.tickets.length > 0) {
      
      this.updateActivityFromForm();
      // checks for time conflict with the current activities of the day
      if (isActivityWithTimeOverlapping(this.activityMuseum, this.currentDay, this.activityEditIndex)) {
      const timeConflictConfirmationDialog = this.translateService.instant("trips.activity-time-conflict");
      const dialogRef = this.confirmationDialog.open(ConfirmationDialogComponent, {
        width: '300px',
        data: { message: timeConflictConfirmationDialog }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          // if confirmed close the main edit dialog
          this.dialogRef.close(this.activityMuseum);
        }
      });
    } else {
      // in case the activity doesn't overlaps close the dialog
      this.dialogRef.close(this.activityMuseum);
    }
  }
}

  cancel() {
    this.dialogRef.close();
  }

  updateActivityFromForm() {
    if (this.activityMuseum && this.form && this.tickets.length > 0) {
      this.activityMuseum.description = this.form.get('description')?.value ?? null;
      this.activityMuseum.notes = this.form.get('notes')?.value ?? null;
      this.activityMuseum.disclaimer = this.form.get('disclaimer')?.value ?? null;
      this.activityMuseum.address = this.form.get('address')?.value ?? null;
      this.activityMuseum.startTime = this.form.get('startTime')?.value ?? null;
      this.activityMuseum.endTime = this.form.get('endTime')?.value ?? null;
      this.activityMuseum.fullPrice = this.form.get('fullPrice')?.value ?? 0;
      this.activityMuseum.reducedPrice = this.form.get('reducedPrice')?.value ?? 0;
      this.activityMuseum.tickets = this.tickets;
      this.activityMuseum.totalPrice = this.calculateTotalPrice();

    }
  }

  // calculate the total price of the tickets
  calculateTotalPrice(): number {
    if (this.activityMuseum) {
      const fullPrice = this.activityMuseum.fullPrice || 0;
      const reducedPrice = this.activityMuseum.reducedPrice || 0;

      const totalFullTickets = this.tickets.filter(ticket => ticket.ticketType === 'full')
        .reduce((sum, ticket) => sum + (ticket.numberOfTickets * fullPrice), 0);

      const totalReducedTickets = this.tickets.filter(ticket => ticket.ticketType === 'reduced')
        .reduce((sum, ticket) => sum + (ticket.numberOfTickets * reducedPrice), 0);

      return totalFullTickets + totalReducedTickets;
    }
    else {
      return 0;
    }
  }

}
